import { CellClassParams, CellStyle } from 'ag-grid-community';
import { PnlGridRow } from 'src/components/star-grid/starGridTypes';

const AXIS_INDENT_WIDTH = 12;
const AXIS_LABEL_PADDING = 8;
const AXIS_SPACER_HEIGHT = 20;
const AXIS_HEADER_PADDING = 10;

export const pnlAxisRowClass = (row?: PnlGridRow): string | undefined =>
  // KIND before emphasis: the deck bolds its section titles, so testing `axis_emphasis` first
  // resolved every heading to `sg-pnl-axis-total` and it never received the header class — which is
  // what carries the section marker, the top padding and the suppressed row rule.
  row?.axis_kind === 'header'
    ? 'sg-pnl-axis-header'
    : row?.axis_kind === 'spacer'
    ? 'sg-pnl-axis-spacer'
    : row?.axis_emphasis
    ? 'sg-pnl-axis-total'
    : undefined;

export const pnlAxisRowHeight = (row: PnlGridRow | undefined, height: number): number | undefined => {
  if (!row?.axis_kind) return undefined;
  if (row.axis_kind === 'spacer') return AXIS_SPACER_HEIGHT;
  return row.axis_kind === 'header' ? height + AXIS_HEADER_PADDING : height;
};

/**
 * Indent + weight for a statement row's label cell.
 *
 * Reads `axis_indent`/`axis_emphasis` (the mapped axis row) and falls back to the `indent`/
 * `emphasis` the query rows carry, so the same style applies whether the grid was built from the
 * axis payload or from the long rows — the deck's nesting must not depend on which path rendered it.
 */
export const pnlAxisLabelStyle = ({ data }: CellClassParams<PnlGridRow>): CellStyle | undefined => {
  if (!data) return undefined;
  const depth = typeof data.axis_indent === 'number' ? data.axis_indent : typeof data.indent === 'number' ? data.indent : undefined;
  const bold = data.axis_emphasis === true || data.emphasis === 'bold';
  if (depth === undefined && !bold) return undefined;
  return {
    paddingLeft: `${AXIS_LABEL_PADDING + Math.max(0, depth ?? 0) * AXIS_INDENT_WIDTH}px`,
    ...(bold ? { fontWeight: 700 } : {})
  };
};

/** The second row column's own depth, when the engine states one (`secondary_indent`). */
export const pnlSecondaryLabelStyle = ({ data }: CellClassParams<PnlGridRow>): CellStyle | undefined =>
  typeof data?.axis_secondary_indent === 'number'
    ? { paddingLeft: `${AXIS_LABEL_PADDING + Math.max(0, data.axis_secondary_indent) * AXIS_INDENT_WIDTH}px` }
    : undefined;
